import { ref, computed, inject, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { priceFormat } from '@/utils/textUtils'
import { ErrorDisplayTypeEnum, UserRoleEnum } from '@/modules/core/models/enums'
import { PricePeriodEnum } from '@/modules/common/common/models/APP/enums'
import type {
  PricePeriodChoiceType,
  PricePeriodType
} from '@/modules/common/common/models/APP/types'
import type Configurations from '@/modules/common/common/models/APP/Configuration'
import Insurance from '@/modules/common/common/models/APP/Insurance'
import type HiringAnswers from '@/modules/common/hiring/models/APP/HiringAnswers'
import { addDaysToDate } from '@/utils/date.utils'

export function useRepricing<A extends HiringAnswers, I extends Insurance>(
  repricingAnswers: Ref<A>,
  configurations: Configurations
) {
  type CallbackNewRaterPrice = (
    answers: A,
    errorDisplayType: ErrorDisplayTypeEnum,
    isQuote?: boolean,
    productCode?: string | null
  ) => Promise<I | null>
  type CallbackHireProduct = (
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: I,
    role: UserRoleEnum,
    endDate?: Date | null | undefined
  ) => Promise<{ valid: boolean }>

  const { t } = useI18n()

  const validateForm = inject<() => Promise<boolean>>('validateForm') ?? (async () => true)

  const dataToRepricing = {
    quotationId: repricingAnswers.value.quotationId,
    quotationNumber: repricingAnswers.value.product!.quotationNumber
  }

  const askToCalculateInsurance = ref(false)
  const price = ref<string | undefined>(undefined)
  const changeData = ref(false)

  const paymentPeriodChoices: PricePeriodChoiceType[] = [
    {
      value: PricePeriodEnum.monthly,
      label: deleteTags(t('common.price-period-choices.monthly'))
    },
    {
      value: PricePeriodEnum.quarterly,
      label: deleteTags(t('common.price-period-choices.quarterly'))
    },
    {
      value: PricePeriodEnum.semiannual,
      label: deleteTags(t('common.price-period-choices.semiannual'))
    },
    {
      value: PricePeriodEnum.annual,
      label: deleteTags(t('common.price-period-choices.annual'))
    }
  ]

  const paymentUnits = computed(() => repricingAnswers.value?.product?.units)

  const period = computed(() => {
    const billingCycle = repricingAnswers.value.billingCycle as PricePeriodType
    return configurations.billingCycles[billingCycle]?.textInPrice
  })

  function deleteTags(text: string) {
    return text.replace(/<\/?[^>]+(>|$)/g, function (match) {
      return match.startsWith('</') ? ')' : '('
    })
  }

  function initializeCommon(answers: A) {
    repricingAnswers.value.startDate = answers.startDate ?? addDaysToDate(1)
    repricingAnswers.value.insureds = answers.insureds
    repricingAnswers.value.product = new Insurance(answers?.product ?? {})
  }

  function resetAll(answers: A) {
    repricingAnswers.value.startDate = answers.startDate ?? addDaysToDate(1)
    repricingAnswers.value.insureds = answers.insureds
    repricingAnswers.value.product = new Insurance(answers?.product ?? {})
    repricingAnswers.value.billingCycle = answers.billingCycle
    repricingAnswers.value.quotationId = answers.quotationId
    repricingAnswers.value.product!.quotationNumber = answers.product?.quotationNumber ?? null
    askToCalculateInsurance.value = false
    price.value = undefined
    changeData.value = false
  }

  async function simulateRepricingCommon(callbackNewRaterPrice: CallbackNewRaterPrice) {
    const isValid = await validateForm()
    if (!isValid) return

    repricingAnswers.value.quotationId = null
    repricingAnswers.value.product!.quotationNumber = null

    const product = await callbackNewRaterPrice(
      repricingAnswers.value,
      ErrorDisplayTypeEnum.TOAST_IN_MODAL,
      false,
      repricingAnswers.value.product!.productCode
    )
    if (!product) throw new Error('Product not found')

    const billingCycle = repricingAnswers.value?.billingCycle
    const newPrice = product?.prices[billingCycle as PricePeriodType]
    price.value = priceFormat(newPrice)

    askToCalculateInsurance.value = true
    changeData.value = false
  }

  async function repricingCommon(
    callbackNewRaterPrice: CallbackNewRaterPrice,
    callbackHireProduct: CallbackHireProduct
  ) {
    repricingAnswers.value.quotationId = dataToRepricing.quotationId
    repricingAnswers.value.product!.quotationNumber = dataToRepricing.quotationNumber

    const product = await callbackNewRaterPrice(
      repricingAnswers.value,
      ErrorDisplayTypeEnum.TOAST_IN_MODAL
    )
    if (!product) throw new Error('Product not found')

    const billingCycle = repricingAnswers.value?.billingCycle
    const { quotationId, startDate, holder } = repricingAnswers.value!
    await callbackHireProduct(
      quotationId,
      startDate,
      billingCycle as string,
      product,
      holder.role,
      (repricingAnswers.value as any).endDate
    )
  }

  return {
    //* refs
    askToCalculateInsurance,
    price,
    changeData,

    //* computed
    period,
    paymentUnits,
    paymentPeriodChoices,

    //* methods
    initializeCommon,
    resetAll,
    simulateRepricingCommon,
    repricingCommon
  }
}
